import { supabase } from './supabase';

/**
 * Award points to the current user.
 * @param {object} opts
 * @param {string} opts.action       - short key e.g. 'lesson_complete'
 * @param {number} opts.points       - number of points to award (positive)
 * @param {string} [opts.description] - human-readable label for history
 * @param {string} [opts.dedupKey]   - if set, this award is issued at most once per user
 * @returns {Promise<{totalPoints, tier, awarded} | null>}
 */
export async function awardPoints({ action, points, description, dedupKey }) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const res = await fetch('/api/points?op=award', {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        Authorization:   `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ action, points, description, dedupKey }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      console.warn('[awardPoints] failed:', err.error);
      return null;
    }

    return await res.json(); // { totalPoints, tier, awarded }
  } catch (err) {
    console.error('[awardPoints]', err.message);
    return null;
  }
}

/**
 * Redeem points for a reward.
 * @param {string} rewardId - e.g. 'extra_lesson', 'free_month_basic'
 * @returns {Promise<{totalPoints, tier, reward, status} | null>}
 */
export async function redeemPoints(rewardId) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return null;

    const res = await fetch('/api/points?op=redeem', {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        Authorization:   `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ rewardId }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Redemption failed');
    return data;
  } catch (err) {
    console.error('[redeemPoints]', err.message);
    throw err; // re-throw so UI can show the error
  }
}

// ── Tier metadata (mirrors server-side constants) ──────────────────────────

export const TIER_META = {
  learner:  { label: 'Learner',  color: '#94a3b8', min: 0,     next: 500   },
  explorer: { label: 'Explorer', color: '#0EA5E9', min: 500,   next: 1500  },
  scholar:  { label: 'Scholar',  color: '#7C3AED', min: 1500,  next: 4000  },
  master:   { label: 'Master',   color: '#f59e0b', min: 4000,  next: 10000 },
  legend:   { label: 'Legend',   color: '#ef4444', min: 10000, next: null  },
};

export function getTierMeta(tier) {
  return TIER_META[tier] || TIER_META.learner;
}

// Streak milestone → points to award
export const STREAK_MILESTONE_PTS = { 3: 15, 7: 50, 14: 100, 30: 250, 60: 500, 100: 1000 };
